import { message } from 'antd';
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_BASE_URL || '';

// Set default axios configuration for all requests
axios.defaults.withCredentials = true;

// Create a function to get the setAccessToken function
let setAccessToken: ((token: string | null) => void) | null = null;
export const setAuthContext = (setter: (token: string | null) => void) => {
  setAccessToken = setter;
};

// Create a function to get the current access token
let getAccessToken: (() => string | null) | null = null;
export const setGetAccessToken = (getter: () => string | null) => {
  getAccessToken = getter;
};

const internalApi = axios.create({
  baseURL: baseURL,
  withCredentials: true, // Send cookies like refresh token
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Add request interceptor to handle CORS preflight and add Authorization header
internalApi.interceptors.request.use(
  config => {
    // Ensure credentials are included in CORS requests
    config.withCredentials = true;
    // Ensure CORS mode is set to include credentials
    config.headers['Access-Control-Allow-Credentials'] = true;

    // Add Authorization header if we have an access token
    const token = getAccessToken?.();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Automatically try to refresh token on 401
internalApi.interceptors.response.use(
  res => res,
  async error => {
    const originalRequest = error.config;
    
    // Handle CORS errors
    if (error.message === 'Network Error' && !error.response) {
      console.error('CORS or Network Error:', error);
      return Promise.reject(new Error('Unable to connect to the server. Please check your network connection.'));
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refresh = await internalApi.post('/admin/refresh-token', {}, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Access-Control-Allow-Credentials': true
          }
        });
        
        const newToken = refresh.data.access_token;
        internalApi.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        
        // Update the token in AuthContext if available
        if (setAccessToken) {
          setAccessToken(newToken);
        }
        
        return internalApi(originalRequest);
      } catch (err: any) {
        console.error('Token refresh failed:', err);
        // Clear the token in AuthContext if available
        if (setAccessToken) {
          setAccessToken(null);
        }
        message.error('Session expired. Please login again.');
        
        if (err?.message === 'Network Error') {
          return Promise.reject(new Error('Unable to refresh session. Please try logging in again.'));
        }
      }
    }
    return Promise.reject(error);
  }
);

export const api = internalApi;